import { Injectable } from '@angular/core';

import { Observable, combineLatestAll, map, merge, of } from 'rxjs';

import { NumpadDisplayService } from '../../../../lib/numpad/numpad-display.service';
import { HideMinError } from '../../stake/utils';
import { BetslipError } from '../errors/betslip-error';
import { GroupLinearPickError, GroupMaxSelectionsError, GroupMaxSelectionsPreCheckError, GroupPickError } from '../errors/general/group-pick-error';
import { ErrorViewModel } from '../models/models';
import { BetslipErrorMessageFormatterService } from './formatting/betslip-error-message-formatter.service';
import { isPickStatusChangeError, isUnderMinimumStakeErrorPreCheck } from './utils/betslip-errors-utils';

@Injectable()
export class ErrorViewModelService {
    constructor(
        private formatterService: BetslipErrorMessageFormatterService,
        private numpadDisplayService: NumpadDisplayService,
    ) {}

    getErrorViewModels$(errors: BetslipError[]): Observable<ErrorViewModel[]> {
        if (!errors.length) {
            return of([]);
        }

        const filteredErrors = this.filterDuplicateErrors(errors);

        return merge(filteredErrors.map((error) => this.formatterService.getErrorMessage(error).pipe(map((message) => ({ error, message }))))).pipe(
            combineLatestAll(),
        );
    }

    getFilteredErrorViewModels$(errors: BetslipError[]): Observable<ErrorViewModel[]> {
        if (!errors.length) {
            return of([]);
        }
        const filteredErrors = this.filterDuplicateErrors(errors);

        return this.getErrorViewModels$(filteredErrors);
    }

    getErrorViewModelsForSlip$(errors: BetslipError[] | undefined, stake: string | null): Observable<ErrorViewModel[]> {
        if (!errors?.length) {
            return of([]);
        }

        let filteredErrors = errors.filter(
            (e) =>
                !isPickStatusChangeError(e) &&
                (!isUnderMinimumStakeErrorPreCheck(e) || !HideMinError(e.minimumStake.toNumber(), this.numpadDisplayService.isOpen(), stake)),
        );

        filteredErrors = this.filterDuplicateErrors(filteredErrors);

        return this.getErrorViewModels$(filteredErrors);
    }

    // we trying to identify duplicate errors - errors that have the same message - and leave
    // just one of them, since we don't want to show the same message multiple times
    // Ex. this is the case for several GroupPick errors
    private filterDuplicateErrors(errors: BetslipError[]): BetslipError[] {
        const isGroupMaxSelectionsError = (error: BetslipError) =>
            error instanceof GroupMaxSelectionsError || error instanceof GroupMaxSelectionsPreCheckError;

        const isCommonGroupPickError = (error: BetslipError) =>
            (error instanceof GroupPickError || error instanceof GroupLinearPickError) && !isGroupMaxSelectionsError(error);

        const firstCommonGroupPickErrorIndex = errors.findIndex(isCommonGroupPickError);
        const firstMaxSelectionGroupPickErrorIndex = errors.findIndex(isGroupMaxSelectionsError);

        return errors.filter(
            (error, index) =>
                (!isCommonGroupPickError(error) || index === firstCommonGroupPickErrorIndex) &&
                (!isGroupMaxSelectionsError(error) || index === firstMaxSelectionGroupPickErrorIndex),
        );
    }
}
